import { FC, memo, useCallback } from 'react'

import { Form } from 'brief-form'
import { countries } from 'commons/types/dictionaries'
import { determineIssuingStateData } from 'commons/utils'
import { InfoSet } from 'ui/components/Info'
import { TextInput } from 'ui/components/InputV2'
import { Select } from 'ui/components/Select'
import { Col, FormRow } from 'ui/themes/globalStyles'

import { OnboardingFormFieldNames } from '../../enums'
import {
  CompanyMailingAddressChangeHandler,
  CompanyMailingAddressFormData,
  CompanyMailingAddressFormValue
} from '../../types/forms'
import { useCompanyMailingAddressForm } from './useCompanyPhysicalAddressForm'

type CompanyMailingAddressFormProps = {
  companyIndex: number
  value: CompanyMailingAddressFormData
  onChange: CompanyMailingAddressChangeHandler
}

export const CompanyMailingAddressForm: FC<CompanyMailingAddressFormProps> = memo((props) => {
  const { companyIndex, value, onChange } = props
  const { Field, config } = useCompanyMailingAddressForm({ companyIndex, formData: value, onChange })
  const originalChangeHandler = config.onChange

  config.onChange = useCallback(
    (v: CompanyMailingAddressFormValue, e) => {
      if (config.value.mailingCountry && config.value.mailingCountry !== v.mailingCountry) {
        v.mailingState = undefined
        delete e['mailingState']
      }
      originalChangeHandler(v, e)
    },
    [originalChangeHandler, config.value.mailingCountry]
  )

  return (
    <Form config={config}>
      <InfoSet legend="Mailing Address">
        <FormRow>
          <Col>
            <Field
              name={OnboardingFormFieldNames.MAILING_STREET}
              label="Street"
              input={TextInput}
              inputProps={{
                'data-qa': 'Street',
                maxLength: 255
              }}
            />
          </Col>
          <Col>
            <Field
              name={OnboardingFormFieldNames.MAILING_CITY}
              label="City"
              input={TextInput}
              inputProps={{
                'data-qa': 'City',
                maxLength: 40
              }}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col>
            <Field
              name={OnboardingFormFieldNames.MAILING_COUNTRY}
              label="Country"
              input={Select}
              inputProps={{
                'data-qa': 'Country',
                'options-data-qa': 'Country',
                data: countries
              }}
            />
          </Col>
          <Col>
            <Field
              name={OnboardingFormFieldNames.MAILING_STATE}
              label="State/Province"
              input={Select}
              inputProps={{
                'data-qa': 'State/Province',
                'options-data-qa': 'State/Province',
                data: determineIssuingStateData(config.value.mailingCountry)
              }}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col>
            <Field
              name={OnboardingFormFieldNames.MAILING_POSTAL_CODE}
              label="Zip/Postal Code"
              input={TextInput}
              inputProps={{
                'data-qa': 'Zip/Postal Code',
                maxLength: 20
              }}
            />
          </Col>
        </FormRow>
      </InfoSet>
    </Form>
  )
})
